import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import RichTextEditor from './RichTextEditor';
import { uploadImage, generateSeoTitle, generateSeoDescription, generateSeoKeywords } from '../../services/propertyService';
import { fetchBiancaProdutos } from './biancaDataService';
import { listCorretores } from '../../services/corretorService';

// ============ Themes (sync with AdminPageV2) ============
const lightTheme = {
  mode: 'light',
  bg: '#FAFAF7', surface: '#FFFFFF', surfaceAlt: '#F5F3EE',
  ink: '#1A1F2B', inkSoft: '#3D434F', muted: '#6B7280',
  line: '#E8E5DE', gold: '#B8935A', goldSoft: '#F2EBDD',
  ok: '#2F7D5B', warn: '#B8935A', danger: '#9A3B3B',
  shadow: 'rgba(0,0,0,.06)',
};
const darkTheme = {
  mode: 'dark',
  bg: '#0F1117', surface: '#181B23', surfaceAlt: '#1F2330',
  ink: '#F1ECE2', inkSoft: '#C9C4B8', muted: '#8A8F9A',
  line: '#262A36', gold: '#C9A06A', goldSoft: 'rgba(201,160,106,.14)',
  ok: '#5BB089', warn: '#C9A06A', danger: '#C5544A',
  shadow: 'rgba(0,0,0,.4)',
};
const THEME_KEY = 'mf_admin_v2_theme';
const VIDEO_RE = /\.(mp4|mov|webm|avi|mkv|m4v)(\?|$)/i;
const isVideo = (u) => VIDEO_RE.test(u || '');

// ============ Styled ============
const Page = styled.div`
  min-height: 100vh;
  background: ${p => p.theme.bg};
  color: ${p => p.theme.ink};
  font-family: 'Inter', -apple-system, sans-serif;
  padding-bottom: 60px;
`;
const StickyHeader = styled.header`
  position: sticky;
  top: 0;
  z-index: 50;
  background: ${p => p.theme.surface};
  border-bottom: 1px solid ${p => p.theme.line};
  backdrop-filter: blur(8px);
`;
const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 32px;
  flex-wrap: wrap;
  @media (max-width: 600px) { padding: 12px 16px; }
`;
const TitleBox = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
  h1 {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 26px;
    font-weight: 600;
    margin: 0;
    color: ${p => p.theme.ink};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .crumb {
    font-size: 11px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: ${p => p.theme.muted};
  }
`;
const BackBtn = styled.button`
  border: 1px solid ${p => p.theme.line};
  background: transparent;
  color: ${p => p.theme.inkSoft};
  width: 38px;
  height: 38px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: ${p => p.theme.surfaceAlt}; color: ${p => p.theme.ink}; }
`;
const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;
const ActionBtn = styled.button`
  border: 1px solid ${p => p.theme.line};
  background: ${p => {
    if (p.$primary) return p.theme.ink;
    if (p.$danger) return 'transparent';
    return p.theme.surface;
  }};
  color: ${p => {
    if (p.$primary) return p.theme.bg;
    if (p.$danger) return p.theme.danger;
    return p.theme.ink;
  }};
  ${p => p.$danger && `border-color: ${p.theme.danger};`}
  padding: 9px 18px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.3px;
  cursor: pointer;
  font-family: inherit;
  transition: all .15s;
  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 2px 8px ${p => p.theme.shadow}; }
  &:disabled { opacity: .5; cursor: not-allowed; }
`;
const Tabs = styled.div`
  display: flex;
  gap: 0;
  padding: 0 32px;
  overflow-x: auto;
  border-top: 1px solid ${p => p.theme.line};
  background: ${p => p.theme.surface};
  &::-webkit-scrollbar { height: 0; }
  @media (max-width: 600px) { padding: 0 12px; }
`;
const Tab = styled.button`
  border: none;
  background: transparent;
  color: ${p => p.$active ? p.theme.ink : p.theme.muted};
  padding: 14px 20px;
  font-size: 13px;
  font-weight: ${p => p.$active ? 600 : 500};
  letter-spacing: 0.3px;
  cursor: pointer;
  font-family: inherit;
  border-bottom: 2px solid ${p => p.$active ? p.theme.gold : 'transparent'};
  white-space: nowrap;
  transition: color .15s;
  &:hover { color: ${p => p.theme.ink}; }
  small {
    margin-left: 6px;
    font-size: 11px;
    color: ${p => p.theme.muted};
    font-weight: 400;
  }
`;
const Body = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px;
  @media (max-width: 600px) { padding: 20px 14px; }
`;
const Section = styled.section`
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.line};
  border-radius: 10px;
  padding: 24px 28px;
  margin-bottom: 18px;
  @media (max-width: 600px) { padding: 18px 16px; }
`;
const SecTitle = styled.h2`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 22px;
  font-weight: 600;
  color: ${p => p.theme.ink};
  margin: 0 0 4px;
  &::after {
    content: '';
    display: block;
    width: 30px;
    height: 2px;
    background: ${p => p.theme.gold};
    margin-top: 6px;
  }
`;
const SecHint = styled.p`
  font-size: 12px;
  color: ${p => p.theme.muted};
  margin: 0 0 18px;
  letter-spacing: 0.2px;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$template || `repeat(${p.$cols || 3}, 1fr)`};
  gap: 16px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;
const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${p => p.$full && 'grid-column: 1 / -1;'}
  font-size: 10px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: ${p => p.theme.muted};
  font-weight: 500;
  input, select, textarea {
    background: ${p => p.theme.bg};
    border: 1px solid ${p => p.theme.line};
    color: ${p => p.theme.ink};
    border-radius: 7px;
    padding: 10px 12px;
    font-size: 14px;
    font-family: inherit;
    letter-spacing: 0.2px;
    text-transform: none;
    font-weight: 400;
    outline: none;
    transition: border-color .15s, box-shadow .15s;
    &:focus {
      border-color: ${p => p.theme.gold};
      box-shadow: 0 0 0 3px ${p => p.theme.goldSoft};
    }
  }
  textarea { resize: vertical; min-height: 80px; }
`;
const ToggleField = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  background: ${p => p.$on ? p.theme.goldSoft : p.theme.bg};
  border: 1px solid ${p => p.$on ? p.theme.gold : p.theme.line};
  border-radius: 8px;
  padding: 14px 16px;
  color: ${p => p.theme.ink};
  transition: border-color .15s, background .15s;
  cursor: pointer;
  ${p => p.$full && 'grid-column: 1 / -1;'}
  &:hover { border-color: ${p => p.theme.gold}; }

  .tog-icon {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: ${p => p.theme.goldSoft};
    color: ${p => p.theme.gold};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
  }
  .tog-text {
    flex: 1;
    min-width: 0;
  }
  .tog-title {
    font-size: 14px;
    font-weight: 600;
    color: ${p => p.theme.ink};
    letter-spacing: 0.1px;
  }
  .tog-hint {
    font-size: 11px;
    color: ${p => p.theme.muted};
    margin-top: 2px;
    line-height: 1.4;
  }
`;
const Switch = styled.button`
  width: 38px;
  height: 22px;
  border-radius: 11px;
  border: none;
  background: ${p => p.$on ? p.theme.gold : p.theme.line};
  position: relative;
  cursor: pointer;
  transition: background .15s;
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${p => p.$on ? '18px' : '2px'};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    transition: left .15s;
    box-shadow: 0 1px 3px rgba(0,0,0,.2);
  }
`;
const SubLabel = styled.div`
  font-size: 10px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${p => p.theme.gold};
  font-weight: 600;
  margin: 14px 0 8px;
`;
const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
`;
const ImageBox = styled.div`
  position: relative;
  aspect-ratio: 1;
  background: ${p => p.theme.surfaceAlt};
  border: 2px solid ${p => p.$dropTarget ? p.theme.gold : (p.$active ? p.theme.gold : p.theme.line)};
  border-radius: 8px;
  overflow: hidden;
  cursor: ${p => p.$draggable ? 'grab' : 'pointer'};
  opacity: ${p => p.$dragging ? 0.4 : 1};
  transform: ${p => p.$dropTarget ? 'scale(1.03)' : 'none'};
  transition: transform .12s ease, opacity .12s ease, border-color .12s ease;
  &:active { cursor: ${p => p.$draggable ? 'grabbing' : 'pointer'}; }
  img, video { width: 100%; height: 100%; object-fit: cover; display: block; }
  button.x {
    position: absolute;
    top: 4px; right: 4px;
    width: 22px; height: 22px;
    border: none;
    border-radius: 50%;
    background: rgba(0,0,0,.7);
    color: #fff;
    cursor: pointer;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }
  .cover-tag {
    position: absolute;
    top: 6px; left: 6px;
    background: ${p => p.theme.gold};
    color: #fff;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 1px;
    padding: 2px 6px;
    border-radius: 3px;
  }
  .play {
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: 36px; height: 36px;
    border-radius: 50%;
    background: rgba(0,0,0,.65);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }
`;
const UploadBox = styled.label`
  position: relative;
  aspect-ratio: 1;
  background: ${p => p.theme.bg};
  border: 2px dashed ${p => p.theme.line};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${p => p.theme.muted};
  font-size: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;
  font-weight: 500;
  transition: all .15s;
  &:hover { border-color: ${p => p.theme.gold}; color: ${p => p.theme.gold}; }
  input { display: none; }
`;
const UploadProgress = styled.div`
  margin-bottom: 12px;
  padding: 12px 14px;
  background: ${p => p.theme.bg};
  border: 1px solid ${p => p.theme.line};
  border-radius: 8px;
  .bar {
    height: 6px;
    background: ${p => p.theme.line};
    border-radius: 999px;
    overflow: hidden;
  }
  .fill {
    height: 100%;
    background: ${p => p.theme.gold};
    transition: width .25s ease;
  }
  .meta {
    margin-top: 8px;
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 12px;
    color: ${p => p.theme.muted};
  }
  .name {
    max-width: 60%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .warn {
    margin-top: 4px;
    font-size: 11px;
    color: ${p => p.theme.muted};
    opacity: .8;
  }
`;
const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
`;
const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${p => p.theme.goldSoft};
  color: ${p => p.theme.ink};
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  border: 1px solid ${p => p.theme.gold};
  button {
    border: none;
    background: transparent;
    color: ${p => p.theme.inkSoft};
    cursor: pointer;
    font-size: 14px;
    line-height: 1;
    padding: 0;
    &:hover { color: ${p => p.theme.danger}; }
  }
`;
const InputRow = styled.div`
  display: flex;
  gap: 8px;
  input {
    flex: 1;
    background: ${p => p.theme.bg};
    border: 1px solid ${p => p.theme.line};
    color: ${p => p.theme.ink};
    border-radius: 7px;
    padding: 9px 12px;
    font-size: 14px;
    outline: none;
    &:focus { border-color: ${p => p.theme.gold}; }
  }
  button {
    border: 1px solid ${p => p.theme.gold};
    background: ${p => p.theme.gold};
    color: #fff;
    padding: 9px 16px;
    border-radius: 7px;
    cursor: pointer;
    font-weight: 500;
    font-size: 13px;
    &:hover { transform: translateY(-1px); }
  }
`;
const SuggestList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 10px;
  button {
    border: 1px dashed ${p => p.theme.line};
    background: transparent;
    color: ${p => p.theme.muted};
    padding: 3px 9px;
    border-radius: 999px;
    font-size: 11px;
    cursor: pointer;
    font-family: inherit;
    &:hover { color: ${p => p.theme.gold}; border-color: ${p => p.theme.gold}; border-style: solid; }
  }
`;

// Categoria de amenidades — nuvem agrupada
const CategoryBlock = styled.div`
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px dashed ${p => p.theme.line};
  &:first-of-type { border-top: none; padding-top: 0; }
`;
const CategoryHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  .ic { font-size: 16px; }
  .lbl {
    font-size: 10px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: ${p => p.theme.gold};
    font-weight: 600;
  }
  .cnt {
    font-size: 11px;
    color: ${p => p.theme.muted};
    margin-left: auto;
    letter-spacing: 0.5px;
  }
`;
const TagCloud = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  button {
    border: 1px solid ${p => p.$active ? p.theme.gold : p.theme.line};
    background: ${p => p.$active ? p.theme.goldSoft : 'transparent'};
    color: ${p => p.$active ? p.theme.ink : p.theme.inkSoft};
    padding: 6px 12px;
    border-radius: 999px;
    font-size: 12px;
    cursor: pointer;
    font-family: inherit;
    font-weight: ${p => p.$active ? 600 : 400};
    transition: all .12s;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
`;
const TagBtn = styled.button`
  border: 1px solid ${p => p.$active ? p.theme.gold : p.theme.line};
  background: ${p => p.$active ? p.theme.goldSoft : 'transparent'};
  color: ${p => p.$active ? p.theme.ink : p.theme.inkSoft};
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  cursor: pointer;
  font-family: inherit;
  font-weight: ${p => p.$active ? 600 : 400};
  transition: all .12s;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  &:hover {
    border-color: ${p => p.theme.gold};
    color: ${p => p.theme.ink};
  }
`;
const Viewer = styled.div`
  position: relative;
  background: #000;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 18px;
  img, video { width: 100%; max-height: 480px; object-fit: contain; display: block; }
  .ctr { position: absolute; top: 12px; left: 12px; background: rgba(0,0,0,.6); color: #fff; padding: 4px 10px; border-radius: 6px; font-size: 12px; }
  .close { position: absolute; top: 12px; right: 12px; width: 32px; height: 32px; border-radius: 50%; background: rgba(0,0,0,.6); color: #fff; border: none; cursor: pointer; font-size: 18px; }
  .nav { position: absolute; top: 50%; transform: translateY(-50%); width: 40px; height: 40px; border-radius: 50%; background: rgba(0,0,0,.5); color: #fff; border: none; cursor: pointer; font-size: 20px; }
  .nav.prev { left: 12px; }
  .nav.next { right: 12px; }
  .bottom { position: absolute; bottom: 12px; left: 50%; transform: translateX(-50%); display: flex; gap: 8px; }
  .bottom button, .bottom span { border: none; background: rgba(0,0,0,.7); color: #fff; padding: 7px 14px; border-radius: 6px; font-size: 12px; cursor: pointer; }
  .bottom .cover { background: ${p => p.theme.gold}; cursor: default; }
`;
const Saved = styled.span`
  font-size: 12px;
  color: ${p => p.theme.ok};
  letter-spacing: 0.5px;
`;

// ============ Map ============
const MapBox = styled.div`
  margin-top: 16px;
  border: 1px solid ${p => p.theme.line};
  border-radius: 10px;
  overflow: hidden;
  height: 320px;
  position: relative;
  .leaflet-container {
    width: 100%;
    height: 100%;
    background: ${p => p.theme.surfaceAlt};
  }
  .leaflet-control-attribution {
    font-size: 9px;
    background: rgba(255,255,255,.7);
  }
`;
const MapBar = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-top: 12px;
  flex-wrap: wrap;
  font-size: 12px;
  color: ${p => p.theme.muted};
  button {
    border: 1px solid ${p => p.theme.gold};
    background: transparent;
    color: ${p => p.theme.gold};
    padding: 7px 14px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    font-family: inherit;
    &:hover { background: ${p => p.theme.goldSoft}; }
    &:disabled { opacity: .5; cursor: not-allowed; }
  }
  .coords { font-family: 'SF Mono', monospace; font-size: 11px; }
`;

// ============ Planta accordion ============
const PlantaCard = styled.div`
  border: 1px solid ${p => p.theme.line};
  border-radius: 10px;
  margin-bottom: 12px;
  background: ${p => p.theme.bg};
  overflow: hidden;
`;
const PlantaHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  cursor: pointer;
  background: ${p => p.theme.surfaceAlt};
  border-bottom: ${p => p.$open ? `1px solid ${p.theme.line}` : 'none'};
  &:hover { background: ${p => p.theme.goldSoft}; }
  .ttl {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 17px;
    font-weight: 600;
    color: ${p => p.theme.ink};
  }
  .meta {
    font-size: 11px;
    color: ${p => p.theme.muted};
    letter-spacing: 0.5px;
    margin-left: 12px;
  }
  .actions {
    display: flex;
    gap: 6px;
  }
  .actions button {
    border: none;
    background: transparent;
    color: ${p => p.theme.muted};
    width: 28px;
    height: 28px;
    border-radius: 50%;
    cursor: pointer;
    font-size: 14px;
    &:hover { color: ${p => p.theme.danger}; background: ${p => p.theme.surface}; }
  }
`;
const PlantaBody = styled.div`
  padding: 18px;
  display: ${p => p.$open ? 'block' : 'none'};
`;
const PlantaImageRow = styled.div`
  display: flex;
  gap: 14px;
  align-items: flex-start;
  margin-top: 12px;
  flex-wrap: wrap;
  .preview {
    width: 140px;
    height: 140px;
    border-radius: 8px;
    background: ${p => p.theme.surfaceAlt};
    border: 1px solid ${p => p.theme.line};
    overflow: hidden;
    img { width: 100%; height: 100%; object-fit: cover; display: block; }
  }
`;
const AddBtn = styled.button`
  width: 100%;
  padding: 14px;
  border: 1px dashed ${p => p.theme.line};
  background: transparent;
  color: ${p => p.theme.gold};
  border-radius: 10px;
  cursor: pointer;
  font-size: 13px;
  letter-spacing: 0.5px;
  font-family: inherit;
  font-weight: 500;
  &:hover { border-color: ${p => p.theme.gold}; background: ${p => p.theme.goldSoft}; }
`;

// ============ SEO V2 ============
const SeoTabs = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 18px;
  border-bottom: 1px solid ${p => p.theme.line};
`;
const SeoTab = styled.button`
  border: none;
  background: transparent;
  color: ${p => p.$active ? p.theme.ink : p.theme.muted};
  padding: 10px 18px;
  font-size: 12px;
  font-weight: ${p => p.$active ? 600 : 500};
  letter-spacing: 1px;
  text-transform: uppercase;
  cursor: pointer;
  font-family: inherit;
  border-bottom: 2px solid ${p => p.$active ? p.theme.gold : 'transparent'};
  margin-bottom: -1px;
`;
const Counter = styled.span`
  font-size: 10px;
  color: ${p => p.$over ? p.theme.danger : p.theme.muted};
  margin-left: 6px;
  font-weight: 400;
  letter-spacing: 0;
  text-transform: none;
`;
const SeoPreview = styled.div`
  margin-top: 18px;
  border: 1px solid ${p => p.theme.line};
  border-radius: 10px;
  padding: 16px;
  background: ${p => p.theme.bg};
  .label {
    font-size: 10px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: ${p => p.theme.muted};
    text-align: center;
    margin-bottom: 12px;
  }
`;
const GooglePreview = styled.div`
  font-family: Arial, sans-serif;
  .url {
    font-size: 12px;
    color: ${p => p.theme.muted};
  }
  .title {
    font-size: 18px;
    color: #1a0dab;
    line-height: 1.3;
    margin: 4px 0;
    cursor: pointer;
    &:hover { text-decoration: underline; }
  }
  .desc {
    font-size: 13px;
    color: ${p => p.theme.inkSoft};
    line-height: 1.4;
  }
`;
const OgPreview = styled.div`
  border: 1px solid ${p => p.theme.line};
  border-radius: 10px;
  overflow: hidden;
  max-width: 480px;
  margin: 0 auto;
  background: ${p => p.theme.surface};
  .img {
    width: 100%;
    aspect-ratio: 1.91/1;
    background-color: ${p => p.theme.surfaceAlt};
    background-size: cover;
    background-position: center;
  }
  .body {
    padding: 12px 14px;
  }
  .url {
    font-size: 11px;
    text-transform: uppercase;
    color: ${p => p.theme.muted};
    letter-spacing: 0.5px;
  }
  .title {
    font-size: 15px;
    font-weight: 600;
    color: ${p => p.theme.ink};
    margin: 2px 0;
    line-height: 1.3;
  }
  .desc {
    font-size: 13px;
    color: ${p => p.theme.muted};
    line-height: 1.4;
  }
`;

const TABS = [
  { id: 'basico', label: 'Básico' },
  { id: 'localizacao', label: 'Localização' },
  { id: 'specs', label: 'Especificações' },
  { id: 'financeiro', label: 'Financeiro' },
  { id: 'planta', label: 'Planta baixa' },
  { id: 'corretor', label: 'Corretor' },
  { id: 'galeria', label: 'Galeria' },
  { id: 'amenidades', label: 'Amenidades' },
  { id: 'seo', label: 'SEO' },
];

// ============ Phone mask: 5511999999999 -> 55 (11) 9 9999-9999 ============
function formatBRPhone(digits) {
  if (!digits) return '';
  const d = String(digits).replace(/\D/g, '');
  // 55 + DDD(2) + 9 + 4 + 4 = 13 dígitos esperados
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)} (${d.slice(2)}`;
  if (d.length <= 5) return `${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4)}`;
  if (d.length <= 9) return `${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 5)} ${d.slice(5)}`;
  return `${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 5)} ${d.slice(5, 9)}-${d.slice(9, 13)}`;
}

// ============ Leaflet pin (mesmo estilo do site) ============
const pinIcon = L.divIcon({
  html: `
    <svg width="32" height="42" viewBox="0 0 28 38" xmlns="http://www.w3.org/2000/svg" style="display:block;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
      <path d="M14 1 C6.8 1 1 6.8 1 14 C1 24 14 37 14 37 C14 37 27 24 27 14 C27 6.8 21.2 1 14 1 Z" fill="#B8935A" stroke="#FFFFFF" stroke-width="2"/>
      <circle cx="14" cy="14" r="4.5" fill="#FFFFFF"/>
    </svg>`,
  className: 'mf-pin-form',
  iconSize: [32, 42],
  iconAnchor: [16, 41],
});

function MapRecenter({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, map.getZoom(), { animate: true });
  }, [center, map]);
  return null;
}

const AMENIDADES_CATEGORIAS = [
  {
    label: 'Lazer e convívio',
    icon: '🌴',
    items: [
      'Piscina', 'Piscina aquecida', 'Piscina infantil', 'Sauna', 'Spa',
      'Churrasqueira', 'Espaço gourmet', 'Salão de festas', 'Salão de jogos',
      'Cinema', 'Adega climatizada', 'Forno de pizza', 'Quadra de tênis',
      'Quadra poliesportiva', 'Beach tennis', 'Campo de futebol',
    ],
  },
  {
    label: 'Fitness e bem-estar',
    icon: '💪',
    items: [
      'Academia', 'Studio de Pilates', 'Studio de yoga', 'Sala de massagem',
      'Spa', 'Solarium', 'Pista de cooper', 'Bicicletário',
    ],
  },
  {
    label: 'Família e crianças',
    icon: '🧸',
    items: [
      'Playground', 'Brinquedoteca', 'Espaço kids', 'Espaço teen', 'Pet place',
      'Pet care', 'Espaço babá',
    ],
  },
  {
    label: 'Trabalho e serviços',
    icon: '💼',
    items: [
      'Coworking', 'Sala de reunião', 'Espaço delivery', 'Lavanderia coletiva',
      'Mercadinho 24h', 'Bicicletário', 'Carregador elétrico',
    ],
  },
  {
    label: 'Segurança e portaria',
    icon: '🛡',
    items: [
      'Portaria 24h', 'Portaria blindada', 'Vigilância patrimonial',
      'Câmeras CFTV', 'Concierge', 'Controle de acesso biométrico',
      'Cerca elétrica',
    ],
  },
  {
    label: 'Conforto do imóvel',
    icon: '🏠',
    items: [
      'Mobiliado', 'Decorado', 'Ar condicionado', 'Aquecimento central',
      'Closet master', 'Varanda gourmet', 'Lareira', 'Hidromassagem',
      'Cozinha planejada', 'Armários planejados', 'Home office',
      'Lavabo', 'Dependência de empregada',
    ],
  },
  {
    label: 'Diferenciais e vistas',
    icon: '✨',
    items: [
      'Vista panorâmica', 'Vista para o mar', 'Vista verde',
      'Cobertura', 'Andar alto', 'Pé-direito duplo', 'Elevador privativo',
      'Elevador', 'Gerador completo', 'Vaga dupla', 'Vaga coberta',
    ],
  },
];

export default function FormPageV2({
  form, setForm, set, editId,
  saving, savedFlash,
  onSave, onDelete, onBack,
  onToggleStatus, onToggleDestaque, toggleBool,
  viewerIndex, setViewerIndex,
  handleGalleryFiles, removeImage, setAsCover, reorderImages, reapplyWatermarkAt,
  galleryUploading, galleryProgress,
  amenidadeInput, setAmenidadeInput, addAmenidade, removeAmenidade,
  handleCepLookup, formatCep,
  fmtMoney, setMoney,
  previewHref,
  SeoSection,
  amenidadesUsadasNoutrosImoveis = [],
  formMode = 'admin', // 'admin' | 'corretor' — corretor esconde campos so-admin
}) {
  const isCorretor = formMode === 'corretor';
  const [tab, setTab] = useState('basico');
  const [corretoresOpts, setCorretoresOpts] = useState([]);
  useEffect(() => {
    let cancelled = false;
    listCorretores({ status: 'aprovado' })
      .then(list => { if (!cancelled) setCorretoresOpts(list || []); })
      .catch(err => console.warn('[FormV2] falha listando corretores:', err));
    return () => { cancelled = true; };
  }, []);
  const selectedCorretor = useMemo(
    () => corretoresOpts.find(c => c.id === form.corretor_id) || null,
    [corretoresOpts, form.corretor_id]
  );
  const onSelectCorretor = (corretorId) => {
    if (!corretorId) {
      setForm(f => ({ ...f, corretor_id: null }));
      return;
    }
    const c = corretoresOpts.find(x => x.id === corretorId);
    if (!c) return;
    setForm(f => ({
      ...f,
      corretor_id: c.id,
      corretor_nome: c.nome || '',
      corretor_telefone: (c.telefone || '').replace(/\D/g, ''),
      comissao_percentual: c.comissao_split != null ? String(c.comissao_split) : f.comissao_percentual,
    }));
  };

  const [mode] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    return localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light';
  });
  const theme = mode === 'dark' ? darkTheme : lightTheme;

  const photos = (form.imagens || []).map((u, i) => ({ u, i })).filter(({ u }) => !isVideo(u));
  const videos = (form.imagens || []).map((u, i) => ({ u, i })).filter(({ u }) => isVideo(u));

  const [dragIdx, setDragIdx] = useState(null);
  const [dropIdx, setDropIdx] = useState(null);

  const onDragStartMedia = (e, idx) => {
    setDragIdx(idx);
    try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(idx)); } catch (_) {}
  };
  const onDragOverMedia = (e, idx) => {
    if (dragIdx == null) return;
    e.preventDefault();
    try { e.dataTransfer.dropEffect = 'move'; } catch (_) {}
    if (dropIdx !== idx) setDropIdx(idx);
  };
  const onDropMedia = (e, idx) => {
    e.preventDefault();
    const from = dragIdx;
    setDragIdx(null);
    setDropIdx(null);
    if (from == null || from === idx) return;
    if (typeof reorderImages === 'function') reorderImages(from, idx);
  };
  const onDragEndMedia = () => { setDragIdx(null); setDropIdx(null); };

  const tabsWithCount = TABS
    .filter(t => !(isCorretor && (t.id === 'corretor' || t.id === 'seo')))
    .map(t => {
      if (t.id === 'galeria') return { ...t, count: (form.imagens || []).length };
      if (t.id === 'amenidades') return { ...t, count: (form.amenidades || []).length };
      if (t.id === 'planta') return { ...t, count: (form.plantas || []).length };
      return t;
    });

  // ===== Map state =====
  const fallbackCoords = [-23.4856, -46.8533]; // Alphaville
  const hasCoords = form.latitude != null && form.longitude != null;
  const mapCenter = hasCoords ? [Number(form.latitude), Number(form.longitude)] : fallbackCoords;
  const [geocoding, setGeocoding] = useState(false);

  const setMarkerCoords = (lat, lng) => {
    setForm(f => ({ ...f, latitude: Number(lat.toFixed(6)), longitude: Number(lng.toFixed(6)) }));
  };

  const searchAddressOnMap = async () => {
    const parts = [form.endereco, form.numero, form.bairro, form.cidade, 'Brasil'].filter(Boolean);
    const q = parts.join(', ');
    if (!q.trim()) {
      alert('Preencha o endereço primeiro.');
      return;
    }
    setGeocoding(true);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`;
      const res = await fetch(url, { headers: { 'Accept-Language': 'pt-BR' } });
      const data = await res.json();
      if (data && data[0]) {
        setMarkerCoords(parseFloat(data[0].lat), parseFloat(data[0].lon));
      } else {
        alert('Endereço não encontrado no mapa. Posicione o pino manualmente.');
      }
    } catch (e) {
      alert('Erro ao buscar endereço: ' + e.message);
    } finally {
      setGeocoding(false);
    }
  };

  // ===== Planta handlers =====
  const addPlanta = () => {
    setForm(f => ({
      ...f,
      plantas: [...(f.plantas || []), { nome: '', preco: '', area: '', quartos: '', banheiros: '', descricao: '', imagem: '' }],
    }));
  };
  const updatePlanta = (idx, patch) => {
    setForm(f => ({
      ...f,
      plantas: (f.plantas || []).map((p, i) => i === idx ? { ...p, ...patch } : p),
    }));
  };
  const removePlanta = (idx) => {
    if (!window.confirm('Remover esta planta?')) return;
    setForm(f => ({
      ...f,
      plantas: (f.plantas || []).filter((_, i) => i !== idx),
    }));
  };
  const [openPlanta, setOpenPlanta] = useState(0);
  const uploadPlantaImage = async (idx, file) => {
    if (!file) return;
    try {
      const url = await uploadImage(file, 'plantas');
      updatePlanta(idx, { imagem: url });
    } catch (e) {
      alert('Erro ao enviar imagem: ' + e.message);
    }
  };

  // ===== SEO state =====
  const [seoTab, setSeoTab] = useState('geral');
  const seoTitle = form.seo_title || generateSeoTitle(form);
  const seoDesc = form.seo_description || generateSeoDescription(form);
  const coverImage = (form.imagens || []).find(u => !isVideo(u)) || '';
  const handleSeoAutofill = () => setForm(f => ({
    ...f,
    seo_title: generateSeoTitle(f),
    seo_description: generateSeoDescription(f),
    seo_keywords: generateSeoKeywords(f),
  }));

  // ===== Telefone com máscara =====
  const phoneDisplay = formatBRPhone(form.corretor_telefone);
  const onPhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 13);
    setForm(f => ({ ...f, corretor_telefone: digits }));
  };

  // ===== Campanhas Bianca (pra seletor) =====
  const [biancaProdutos, setBiancaProdutos] = useState([]);
  useEffect(() => {
    fetchBiancaProdutos().then(setBiancaProdutos).catch(() => {});
  }, []);

  const addAmenidadeFromSuggestion = (a) => {
    if (!form.amenidades.includes(a)) {
      setForm(f => ({ ...f, amenidades: [...f.amenidades, a] }));
    }
  };

  // Coleta amenidades únicas usadas em outros imóveis (que não estão nas categorias estáticas)
  const todasEstaticas = new Set(AMENIDADES_CATEGORIAS.flatMap(c => c.items.map(i => i.toLowerCase())));
  const amenidadesCustomizadasUsadas = (() => {
    const set = new Map(); // lowercase -> display original
    for (const imovel of amenidadesUsadasNoutrosImoveis || []) {
      if (imovel.id === editId) continue; // não incluir as do imóvel atual
      const lista = Array.isArray(imovel.amenidades) ? imovel.amenidades : [];
      for (const a of lista) {
        if (!a) continue;
        const key = String(a).trim().toLowerCase();
        if (!key || todasEstaticas.has(key)) continue;
        if (!set.has(key)) set.set(key, String(a).trim());
      }
    }
    return Array.from(set.values()).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  })();

  return (
    <ThemeProvider theme={theme}>
      <Page>
        <StickyHeader>
          <HeaderTop>
            <TitleBox>
              <BackBtn onClick={onBack} title="Voltar">←</BackBtn>
              <div>
                <div className="crumb">Imóveis · {editId ? 'Editar' : 'Novo'}</div>
                <h1>{form.titulo || (editId ? 'Editar imóvel' : 'Novo imóvel')}</h1>
              </div>
            </TitleBox>
            <Actions>
              {savedFlash && <Saved>Salvo!</Saved>}
              {editId && previewHref && (
                <ActionBtn as="a" href={previewHref} target="_blank" rel="noopener noreferrer">Preview</ActionBtn>
              )}
              {editId && !isCorretor && (
                <>
                  <ActionBtn onClick={() => onToggleStatus({ id: editId, status: form.status })}>
                    {form.status === 'publicado' ? 'Despublicar' : 'Publicar'}
                  </ActionBtn>
                  <ActionBtn onClick={() => onToggleDestaque({ id: editId, destaque: form.destaque })}>
                    {form.destaque ? 'Remover ★' : 'Destacar'}
                  </ActionBtn>
                  <ActionBtn $danger onClick={() => { if (window.confirm('Excluir este imóvel?')) { onDelete(editId); onBack(); } }}>
                    Excluir
                  </ActionBtn>
                </>
              )}
              {editId && isCorretor && (
                <ActionBtn $danger onClick={() => { if (window.confirm('Excluir este imóvel?')) { onDelete(editId); onBack(); } }}>
                  Excluir
                </ActionBtn>
              )}
              <ActionBtn $primary onClick={onSave} disabled={saving || !form.titulo}>
                {saving ? 'Salvando…' : 'Salvar'}
              </ActionBtn>
            </Actions>
          </HeaderTop>
          <Tabs>
            {tabsWithCount.map(t => (
              <Tab key={t.id} $active={tab === t.id} onClick={() => setTab(t.id)}>
                {t.label}{typeof t.count === 'number' && <small>({t.count})</small>}
              </Tab>
            ))}
          </Tabs>
        </StickyHeader>

        <Body>
          {tab === 'basico' && (
            <>
              <Section>
                <SecTitle>Informações básicas</SecTitle>
                <SecHint>Identificação principal do imóvel.</SecHint>
                <Grid $template="3fr 1fr 1fr">
                  <Field>
                    Título
                    <input value={form.titulo} onChange={set('titulo')} placeholder="Ex: Apartamento Gran Alphaville" />
                  </Field>
                  <Field>
                    Tipo
                    <select value={form.tipo} onChange={set('tipo')}>
                      <option value="apartamento">Apartamento</option>
                      <option value="casa">Casa</option>
                      <option value="studio">Studio</option>
                      <option value="cobertura">Cobertura</option>
                      <option value="terreno">Terreno</option>
                      <option value="comercial">Comercial</option>
                    </select>
                  </Field>
                  {!isCorretor && (
                    <Field>
                      Status
                      <select value={form.status} onChange={set('status')}>
                        <option value="rascunho">Rascunho</option>
                        <option value="publicado">Publicado</option>
                        <option value="vendido">Vendido</option>
                        <option value="inativo">Inativo</option>
                      </select>
                    </Field>
                  )}
                </Grid>
              </Section>

              {!isCorretor && (
              <Section>
                <SecTitle>Publicação e visibilidade</SecTitle>
                <SecHint>Controla onde este imóvel aparece — no site MF e em portais externos.</SecHint>
                <Grid $cols={3}>
                  <ToggleField $on={form.destaque} onClick={toggleBool('destaque')}>
                    <span className="tog-icon">★</span>
                    <div className="tog-text">
                      <div className="tog-title">Destaque na home</div>
                      <div className="tog-hint">Aparece em "Imóveis selecionados"</div>
                    </div>
                    <Switch type="button" $on={form.destaque} onClick={(e) => { e.stopPropagation(); toggleBool('destaque')(); }} />
                  </ToggleField>

                  <ToggleField $on={form.incluir_no_feed} onClick={toggleBool('incluir_no_feed')}>
                    <span className="tog-icon">📡</span>
                    <div className="tog-text">
                      <div className="tog-title">Publicar nos portais</div>
                      <div className="tog-hint">Feed XML pra OLX Pro, ZAP, etc.</div>
                    </div>
                    <Switch type="button" $on={form.incluir_no_feed} onClick={(e) => { e.stopPropagation(); toggleBool('incluir_no_feed')(); }} />
                  </ToggleField>

                  <Field>
                    Campanha Bianca CRM
                    <select value={form.bianca_product_id || ''} onChange={set('bianca_product_id')}>
                      <option value="">— Sem vínculo —</option>
                      {biancaProdutos.map(p => (
                        <option key={p.product_id} value={p.product_id}>
                          {p.Nome}{!p.ativo ? ' (inativo)' : ''}
                        </option>
                      ))}
                    </select>
                    <span style={{ fontSize: 11, color: '#6B7280', textTransform: 'none', letterSpacing: 0, marginTop: 4, fontWeight: 400 }}>
                      Leads dessa campanha aparecem agregados no dashboard.
                    </span>
                  </Field>
                </Grid>
              </Section>
              )}

              <Section>
                <SecTitle>Descrição</SecTitle>
                <SecHint>Use formatação para destacar os pontos fortes do imóvel. Negrito, listas, citações ficam visíveis na página pública.</SecHint>
                <RichTextEditor
                  value={form.descricao}
                  onChange={(html) => setForm(f => ({ ...f, descricao: html }))}
                  placeholder="Descreva o imóvel — diferenciais, vista, acabamentos, lazer do condomínio…"
                />
              </Section>
            </>
          )}

          {tab === 'localizacao' && (
            <Section>
              <SecTitle>Localização</SecTitle>
              <SecHint>Comece pelo CEP — ao sair do campo, busca o endereço automaticamente. Depois confirme a posição no mapa antes de salvar.</SecHint>
              <Grid>
                <Field>
                  CEP
                  <input
                    value={formatCep(form.cep)}
                    onChange={(e) => setForm(f => ({ ...f, cep: formatCep(e.target.value) }))}
                    onBlur={handleCepLookup}
                    placeholder="00000-000"
                    maxLength={9}
                  />
                </Field>
                <Field>
                  Endereço (rua)
                  <input value={form.endereco} onChange={set('endereco')} placeholder="Alameda Manhattan" />
                </Field>
                <Field>
                  Número
                  <input value={form.numero} onChange={set('numero')} placeholder="114" />
                </Field>
                <Field>
                  Bairro
                  <input value={form.bairro} onChange={set('bairro')} list="bairros-v2" placeholder="Digite ou selecione" />
                  <datalist id="bairros-v2">
                    <option value="Alphaville" />
                    <option value="Tamboré" />
                    <option value="Barueri Centro" />
                  </datalist>
                </Field>
                <Field>
                  Cidade
                  <input value={form.cidade} onChange={set('cidade')} />
                </Field>
                <Field>
                  Condomínio
                  <input value={form.condominio} onChange={set('condominio')} />
                </Field>
              </Grid>

              <MapBox>
                <MapContainer center={mapCenter} zoom={hasCoords ? 16 : 14} style={{ width: '100%', height: '100%' }}>
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                  <MapRecenter center={mapCenter} />
                  {hasCoords && (
                    <Marker
                      position={mapCenter}
                      icon={pinIcon}
                      draggable
                      eventHandlers={{
                        dragend: (e) => {
                          const { lat, lng } = e.target.getLatLng();
                          setMarkerCoords(lat, lng);
                        },
                      }}
                    />
                  )}
                </MapContainer>
              </MapBox>
              <MapBar>
                <button type="button" onClick={searchAddressOnMap} disabled={geocoding}>
                  {geocoding ? 'Buscando…' : 'Buscar endereço no mapa'}
                </button>
                {hasCoords && (
                  <>
                    <span className="coords">{form.latitude.toFixed(5)}, {form.longitude.toFixed(5)}</span>
                    <button type="button" onClick={() => setForm(f => ({ ...f, latitude: null, longitude: null }))}>
                      Limpar pino
                    </button>
                    <span style={{ fontSize: 11 }}>(arraste o pino pra ajustar)</span>
                  </>
                )}
                {!hasCoords && <span style={{ fontSize: 11 }}>Sem coordenadas — clique em "Buscar endereço no mapa" pra posicionar.</span>}
              </MapBar>
            </Section>
          )}

          {tab === 'planta' && (
            <Section>
              <SecTitle>Planta baixa</SecTitle>
              <SecHint>Adicione plantas baixas dos pisos do empreendimento. Aparecem em uma seção dedicada na página pública.</SecHint>

              {(form.plantas || []).map((p, idx) => (
                <PlantaCard key={idx}>
                  <PlantaHead $open={openPlanta === idx} onClick={() => setOpenPlanta(openPlanta === idx ? -1 : idx)}>
                    <div>
                      <span className="ttl">{p.nome || `Planta ${idx + 1}`}</span>
                      {(p.area || p.quartos) && (
                        <span className="meta">
                          {p.area && `${p.area} m²`}{p.area && p.quartos ? ' · ' : ''}{p.quartos && `${p.quartos} quartos`}
                        </span>
                      )}
                    </div>
                    <div className="actions">
                      <button type="button" onClick={(e) => { e.stopPropagation(); removePlanta(idx); }} title="Remover">×</button>
                    </div>
                  </PlantaHead>
                  <PlantaBody $open={openPlanta === idx}>
                    <Grid>
                      <Field $full>
                        Nome do piso
                        <input value={p.nome} onChange={e => updatePlanta(idx, { nome: e.target.value })} placeholder="Ex: Térreo, 1º Pavimento, Cobertura…" />
                      </Field>
                      <Field>
                        Preço (R$)
                        <input type="text" inputMode="numeric" value={p.preco} onChange={e => updatePlanta(idx, { preco: e.target.value.replace(/\D/g, '') })} placeholder="0" />
                      </Field>
                      <Field>
                        Área (m²)
                        <input type="number" value={p.area} onChange={e => updatePlanta(idx, { area: e.target.value })} placeholder="120" />
                      </Field>
                      <Field>
                        Quartos
                        <input type="number" value={p.quartos} onChange={e => updatePlanta(idx, { quartos: e.target.value })} />
                      </Field>
                      <Field>
                        Banheiros
                        <input type="number" value={p.banheiros} onChange={e => updatePlanta(idx, { banheiros: e.target.value })} />
                      </Field>
                      <Field $full>
                        Descrição
                        <textarea value={p.descricao} onChange={e => updatePlanta(idx, { descricao: e.target.value })} rows={3} placeholder="Detalhes deste piso…" />
                      </Field>
                    </Grid>
                    <PlantaImageRow>
                      <div className="preview">
                        {p.imagem && <img src={p.imagem} alt={p.nome} />}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <UploadBox style={{ aspectRatio: 'auto', padding: '12px 20px', minWidth: 180 }}>
                          {p.imagem ? 'Trocar imagem' : '+ Imagem da planta'}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => uploadPlantaImage(idx, e.target.files?.[0])}
                          />
                        </UploadBox>
                        {p.imagem && (
                          <button
                            type="button"
                            onClick={() => updatePlanta(idx, { imagem: '' })}
                            style={{ background: 'transparent', border: 'none', color: theme.danger, cursor: 'pointer', fontSize: 12, padding: 0 }}
                          >Remover imagem</button>
                        )}
                      </div>
                    </PlantaImageRow>
                  </PlantaBody>
                </PlantaCard>
              ))}

              <AddBtn type="button" onClick={addPlanta}>
                + Adicionar piso
              </AddBtn>
            </Section>
          )}

          {tab === 'specs' && (
            <Section>
              <SecTitle>Especificações</SecTitle>
              <SecHint>Áreas, dormitórios e características principais.</SecHint>
              <Grid $cols={4}>
                <Field>
                  Área Terreno (m²)
                  <input type="number" value={form.area_m2} onChange={set('area_m2')} />
                </Field>
                <Field>
                  Área Construída (m²)
                  <input type="number" value={form.area_construida_m2} onChange={set('area_construida_m2')} />
                </Field>
                <Field>
                  Quartos
                  <input type="number" value={form.quartos} onChange={set('quartos')} />
                </Field>
                <Field>
                  Suítes
                  <input type="number" value={form.suites} onChange={set('suites')} />
                </Field>
                <Field>
                  Banheiros
                  <input type="number" value={form.banheiros} onChange={set('banheiros')} />
                </Field>
                <Field>
                  Vagas
                  <input type="number" value={form.vagas} onChange={set('vagas')} />
                </Field>
                <Field>
                  Andar
                  <input type="number" value={form.andar} onChange={set('andar')} />
                </Field>
                <ToggleField>
                  <span>Mobiliado</span>
                  <Switch type="button" $on={form.mobiliado} onClick={toggleBool('mobiliado')} />
                </ToggleField>
              </Grid>
            </Section>
          )}

          {tab === 'financeiro' && (
            <Section>
              <SecTitle>Financeiro</SecTitle>
              <SecHint>Valores de venda e/ou aluguel. Deixe em branco o que não se aplica.</SecHint>

              <SubLabel>Venda</SubLabel>
              <Grid>
                <Field>
                  Preço (R$)
                  <input type="text" inputMode="numeric" value={fmtMoney(form.preco)} onChange={setMoney('preco')} placeholder="0,00" />
                </Field>
                <Field>
                  Condomínio (R$/mês)
                  <input type="text" inputMode="numeric" value={fmtMoney(form.preco_condominio)} onChange={setMoney('preco_condominio')} placeholder="0,00" />
                </Field>
                <Field>
                  IPTU (R$/mês)
                  <input type="text" inputMode="numeric" value={fmtMoney(form.iptu)} onChange={setMoney('iptu')} placeholder="0,00" />
                </Field>
              </Grid>

              <SubLabel>Aluguel</SubLabel>
              <Grid>
                <Field>
                  Preço (R$/mês)
                  <input type="text" inputMode="numeric" value={fmtMoney(form.preco_aluguel)} onChange={setMoney('preco_aluguel')} placeholder="0,00" />
                </Field>
                <Field>
                  Condomínio (R$/mês)
                  <input type="text" inputMode="numeric" value={fmtMoney(form.preco_condominio_aluguel)} onChange={setMoney('preco_condominio_aluguel')} placeholder="0,00" />
                </Field>
                <Field>
                  IPTU (R$/mês)
                  <input type="text" inputMode="numeric" value={fmtMoney(form.iptu_aluguel)} onChange={setMoney('iptu_aluguel')} placeholder="0,00" />
                </Field>
              </Grid>
            </Section>
          )}

          {tab === 'corretor' && !isCorretor && (
            <Section>
              <SecTitle>Corretor</SecTitle>
              <SecHint>
                Vincule um corretor cadastrado no sistema (o imóvel aparece no painel dele) ou preencha
                manualmente para captações diretas da MF.
              </SecHint>
              <Grid>
                <Field $full>
                  Vincular a corretor cadastrado
                  <select
                    value={form.corretor_id || ''}
                    onChange={(e) => onSelectCorretor(e.target.value || null)}
                  >
                    <option value="">— Captação direta (preencher manual abaixo) —</option>
                    {corretoresOpts.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.nome}{c.creci ? ` · CRECI ${c.creci}${c.creci_uf ? '-' + c.creci_uf : ''}` : ''}
                      </option>
                    ))}
                  </select>
                </Field>
                {selectedCorretor && (
                  <Field $full>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 14px', borderRadius: 8,
                      background: 'rgba(184, 147, 90, .08)', border: '1px solid rgba(184, 147, 90, .25)',
                      textTransform: 'none', letterSpacing: 0, fontSize: 13, color: '#B8935A',
                    }}>
                      <span>Vinculado a <strong>{selectedCorretor.nome}</strong> · campos abaixo refletem o cadastro dele.</span>
                      <button type="button"
                        onClick={() => onSelectCorretor(null)}
                        style={{
                          background: 'transparent', border: '1px solid rgba(184, 147, 90, .35)',
                          color: '#B8935A', padding: '4px 10px', borderRadius: 6,
                          fontSize: 11, letterSpacing: 1.2, textTransform: 'uppercase', cursor: 'pointer',
                        }}
                      >Desvincular</button>
                    </div>
                  </Field>
                )}
                <Field>
                  Nome
                  <input value={form.corretor_nome} onChange={set('corretor_nome')} readOnly={!!form.corretor_id} />
                </Field>
                <Field>
                  Telefone WhatsApp
                  <input value={phoneDisplay} onChange={onPhoneChange} placeholder="55 (11) 9 9999-9999" readOnly={!!form.corretor_id} />
                </Field>
                <Field>
                  Comissão (%)
                  <input type="number" value={form.comissao_percentual} onChange={set('comissao_percentual')} />
                </Field>
                <Field $full>
                  Observações sobre comissão
                  <input value={form.comissao_obs} onChange={set('comissao_obs')} />
                </Field>
              </Grid>
            </Section>
          )}

          {tab === 'galeria' && (
            <>
              {viewerIndex !== null && form.imagens[viewerIndex] && (
                <Viewer>
                  {isVideo(form.imagens[viewerIndex])
                    ? <video src={form.imagens[viewerIndex]} controls autoPlay />
                    : <img src={form.imagens[viewerIndex]} alt={`Mídia ${viewerIndex + 1}`} />
                  }
                  <span className="ctr">{viewerIndex + 1} / {form.imagens.length}</span>
                  <button className="close" onClick={() => setViewerIndex(null)}>×</button>
                  {viewerIndex > 0 && <button className="nav prev" onClick={() => setViewerIndex(i => i - 1)}>‹</button>}
                  {viewerIndex < form.imagens.length - 1 && <button className="nav next" onClick={() => setViewerIndex(i => i + 1)}>›</button>}
                  <div className="bottom">
                    {viewerIndex === 0 && <span className="cover">★ Capa atual</span>}
                    {viewerIndex !== 0 && !isVideo(form.imagens[viewerIndex]) && (
                      <button onClick={() => setAsCover(viewerIndex)}>★ Definir como capa</button>
                    )}
                    <button onClick={() => {
                      const next = viewerIndex >= form.imagens.length - 1 ? viewerIndex - 1 : viewerIndex;
                      removeImage(viewerIndex);
                      setViewerIndex(next >= 0 ? next : null);
                    }}>Excluir</button>
                  </div>
                </Viewer>
              )}

              <Section>
                <SecTitle>Fotos ({photos.length})</SecTitle>
                <SecHint>A primeira foto vira a capa. Arraste pra reordenar. HEIC (iPhone) aceito — convertemos pra WebP com marca d'água.</SecHint>
                {galleryUploading && galleryProgress.total > 0 && (
                  <UploadProgress>
                    <div className="bar">
                      <div
                        className="fill"
                        style={{ width: `${Math.round((galleryProgress.done / galleryProgress.total) * 100)}%` }}
                      />
                    </div>
                    <div className="meta">
                      <span>
                        {galleryProgress.done}/{galleryProgress.total}
                        {galleryProgress.stage ? ` · ${galleryProgress.stage}` : ''}
                      </span>
                      {galleryProgress.fileName && <span className="name" title={galleryProgress.fileName}>{galleryProgress.fileName}</span>}
                    </div>
                    <div className="warn">Não feche essa página enquanto envia.</div>
                  </UploadProgress>
                )}
                <ImageGrid>
                  {photos.map(({ u, i }) => (
                    <ImageBox
                      key={i}
                      $active={viewerIndex === i}
                      $draggable
                      $dragging={dragIdx === i}
                      $dropTarget={dropIdx === i && dragIdx !== null && dragIdx !== i}
                      draggable
                      onDragStart={(e) => onDragStartMedia(e, i)}
                      onDragOver={(e) => onDragOverMedia(e, i)}
                      onDrop={(e) => onDropMedia(e, i)}
                      onDragEnd={onDragEndMedia}
                      onClick={() => setViewerIndex(i)}
                    >
                      <img src={u} alt={`Foto ${i + 1}`} draggable={false} />
                      {i === 0 && <span className="cover-tag">★ CAPA</span>}
                      <button className="x" onClick={(e) => { e.stopPropagation(); removeImage(i); }}>×</button>
                    </ImageBox>
                  ))}
                  <UploadBox>
                    {galleryUploading ? `${galleryProgress.done}/${galleryProgress.total}` : '+ Foto'}
                    <input
                      type="file"
                      accept="image/*,.heic,.heif,image/heic,image/heif"
                      multiple
                      disabled={galleryUploading}
                      onChange={handleGalleryFiles}
                    />
                  </UploadBox>
                </ImageGrid>
              </Section>

              <Section>
                <SecTitle>Vídeos ({videos.length})</SecTitle>
                <SecHint>Vídeos são enviados sem marca d'água. Arraste pra reordenar. Formatos: mp4, webm, mov, mkv.</SecHint>
                <ImageGrid>
                  {videos.map(({ u, i }) => (
                    <ImageBox
                      key={i}
                      $active={viewerIndex === i}
                      $draggable
                      $dragging={dragIdx === i}
                      $dropTarget={dropIdx === i && dragIdx !== null && dragIdx !== i}
                      draggable
                      onDragStart={(e) => onDragStartMedia(e, i)}
                      onDragOver={(e) => onDragOverMedia(e, i)}
                      onDrop={(e) => onDropMedia(e, i)}
                      onDragEnd={onDragEndMedia}
                      onClick={() => setViewerIndex(i)}
                    >
                      <video src={`${u}#t=0.1`} muted playsInline preload="metadata" />
                      <span className="play">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                      </span>
                      <button className="x" onClick={(e) => { e.stopPropagation(); removeImage(i); }}>×</button>
                    </ImageBox>
                  ))}
                  <UploadBox>
                    {galleryUploading ? `${galleryProgress.done}/${galleryProgress.total}` : '+ Vídeo'}
                    <input
                      type="file"
                      accept="video/mp4,video/webm,video/quicktime,video/x-matroska"
                      multiple
                      disabled={galleryUploading}
                      onChange={handleGalleryFiles}
                    />
                  </UploadBox>
                </ImageGrid>
              </Section>
            </>
          )}

          {tab === 'amenidades' && (
            <Section>
              <SecTitle>Amenidades ({(form.amenidades || []).length})</SecTitle>
              <SecHint>Clique nas tags abaixo para adicionar/remover. Ou digite uma personalizada no campo no fim.</SecHint>

              {(form.amenidades || []).length > 0 && (
                <>
                  <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: theme.muted, fontWeight: 600, marginBottom: 8 }}>
                    Selecionadas
                  </div>
                  <ChipRow>
                    {(form.amenidades || []).map((a, i) => (
                      <Chip key={i}>
                        {a}
                        <button onClick={() => removeAmenidade(i)} title="Remover">×</button>
                      </Chip>
                    ))}
                  </ChipRow>
                </>
              )}

              {AMENIDADES_CATEGORIAS.map(cat => {
                const ativosNaCategoria = cat.items.filter(it => (form.amenidades || []).includes(it)).length;
                return (
                  <CategoryBlock key={cat.label}>
                    <CategoryHead>
                      <span className="ic">{cat.icon}</span>
                      <span className="lbl">{cat.label}</span>
                      {ativosNaCategoria > 0 && (
                        <span className="cnt">{ativosNaCategoria} ativa{ativosNaCategoria > 1 ? 's' : ''}</span>
                      )}
                    </CategoryHead>
                    <TagCloud>
                      {cat.items.map(item => {
                        const active = (form.amenidades || []).includes(item);
                        return (
                          <TagBtn
                            key={item}
                            type="button"
                            $active={active}
                            onClick={() => {
                              if (active) {
                                setForm(f => ({ ...f, amenidades: f.amenidades.filter(a => a !== item) }));
                              } else {
                                addAmenidadeFromSuggestion(item);
                              }
                            }}
                          >
                            {active && '✓ '}{item}
                          </TagBtn>
                        );
                      })}
                    </TagCloud>
                  </CategoryBlock>
                );
              })}

              {amenidadesCustomizadasUsadas.length > 0 && (
                <CategoryBlock>
                  <CategoryHead>
                    <span className="ic">📚</span>
                    <span className="lbl">Já usadas em outros imóveis</span>
                    <span className="cnt">{amenidadesCustomizadasUsadas.length}</span>
                  </CategoryHead>
                  <TagCloud>
                    {amenidadesCustomizadasUsadas.map(item => {
                      const active = (form.amenidades || []).includes(item);
                      return (
                        <TagBtn
                          key={item}
                          type="button"
                          $active={active}
                          onClick={() => {
                            if (active) {
                              setForm(f => ({ ...f, amenidades: f.amenidades.filter(a => a !== item) }));
                            } else {
                              addAmenidadeFromSuggestion(item);
                            }
                          }}
                        >
                          {active && '✓ '}{item}
                        </TagBtn>
                      );
                    })}
                  </TagCloud>
                </CategoryBlock>
              )}

              <CategoryBlock>
                <CategoryHead>
                  <span className="ic">＋</span>
                  <span className="lbl">Personalizada</span>
                </CategoryHead>
                <InputRow>
                  <input
                    value={amenidadeInput}
                    onChange={e => setAmenidadeInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addAmenidade())}
                    placeholder="Nova amenidade — depois aparece em 'Já usadas em outros imóveis'…"
                  />
                  <button onClick={addAmenidade}>Adicionar</button>
                </InputRow>
              </CategoryBlock>
            </Section>
          )}

          {tab === 'seo' && !isCorretor && (
            <Section>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <SecTitle>SEO</SecTitle>
                  <SecHint>Como o imóvel aparece no Google e nas redes sociais.</SecHint>
                </div>
                <ActionBtn type="button" onClick={handleSeoAutofill}>Gerar automático</ActionBtn>
              </div>

              <SeoTabs>
                <SeoTab type="button" $active={seoTab === 'geral'} onClick={() => setSeoTab('geral')}>Geral</SeoTab>
                <SeoTab type="button" $active={seoTab === 'social'} onClick={() => setSeoTab('social')}>Social</SeoTab>
              </SeoTabs>

              {seoTab === 'geral' && (
                <>
                  <Grid $cols={1}>
                    <Field>
                      Título SEO
                      <Counter $over={(seoTitle || '').length > 70}>{(seoTitle || '').length}/70</Counter>
                      <input value={form.seo_title || ''} onChange={set('seo_title')} placeholder={generateSeoTitle(form)} />
                    </Field>
                    <Field>
                      Meta description
                      <Counter $over={(seoDesc || '').length > 160}>{(seoDesc || '').length}/160</Counter>
                      <textarea value={form.seo_description || ''} onChange={set('seo_description')} rows={3} placeholder={generateSeoDescription(form)} />
                    </Field>
                    <Field>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase' }}>Palavras-chave</span>
                        <button
                          type="button"
                          onClick={() => {
                            const sugeridas = generateSeoKeywords(form);
                            // sugeridas é string com vírgulas — merge com as existentes
                            const atuais = (form.seo_keywords || '').split(',').map(s => s.trim()).filter(Boolean);
                            const novas = sugeridas.split(',').map(s => s.trim()).filter(Boolean);
                            const merged = Array.from(new Set([...atuais, ...novas]));
                            setForm(f => ({ ...f, seo_keywords: merged.join(', ') }));
                          }}
                          style={{
                            background: 'transparent',
                            border: '1px solid currentColor',
                            color: '#B8935A',
                            padding: '4px 10px',
                            borderRadius: 6,
                            fontSize: 11,
                            letterSpacing: 0.5,
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            fontWeight: 500,
                            textTransform: 'none',
                          }}
                          title="Sugere palavras-chave baseadas no tipo, bairro, cidade e características"
                        >
                          ✨ Sugerir keywords
                        </button>
                      </div>
                      <input value={form.seo_keywords || ''} onChange={set('seo_keywords')} placeholder="separe por vírgula" />
                    </Field>
                  </Grid>

                  <SeoPreview>
                    <div className="label">Preview no Google</div>
                    <GooglePreview>
                      <div className="url">www.mfrealstate.com.br › imoveis</div>
                      <div className="title">{(seoTitle || '—').slice(0, 70)}</div>
                      <div className="desc">{(seoDesc || '—').slice(0, 160)}</div>
                    </GooglePreview>
                  </SeoPreview>
                </>
              )}

              {seoTab === 'social' && (
                <>
                  <SecHint>Quando o link é compartilhado no Facebook, WhatsApp, Twitter etc., esses dados aparecem. Por padrão usa título e descrição da aba Geral.</SecHint>

                  <SeoPreview>
                    <div className="label">Preview Facebook / WhatsApp</div>
                    <OgPreview>
                      <div className="img" style={{ backgroundImage: coverImage ? `url(${coverImage})` : undefined }} />
                      <div className="body">
                        <div className="url">mfrealstate.com.br</div>
                        <div className="title">{(seoTitle || '—').slice(0, 90)}</div>
                        <div className="desc">{(seoDesc || '—').slice(0, 160)}</div>
                      </div>
                    </OgPreview>
                  </SeoPreview>

                  <SeoPreview>
                    <div className="label">Preview Twitter / X</div>
                    <OgPreview>
                      <div className="img" style={{ backgroundImage: coverImage ? `url(${coverImage})` : undefined }} />
                      <div className="body">
                        <div className="url">mfrealstate.com.br</div>
                        <div className="title">{(seoTitle || '—').slice(0, 70)}</div>
                      </div>
                    </OgPreview>
                  </SeoPreview>
                </>
              )}
            </Section>
          )}
        </Body>
      </Page>
    </ThemeProvider>
  );
}
